import { useState, useRef } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Paperclip, X, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline file uploader. Value: list of file meta objects {id, name, mime, size}.
 * onChange(newList) called whenever attachments change.
 */
export default function FileAttach({ value = [], onChange, testIdPrefix = "attach" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const f of files) {
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/files/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(data);
      } catch (err) {
        toast.error((err.response?.data?.detail) || err.message);
      }
    }
    onChange([...(value || []), ...added]);
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };

  const remove = (id) => onChange((value || []).filter(f => f.id !== id));

  return (
    <div className="space-y-2">
      <input ref={ref} type="file" multiple className="hidden" onChange={upload} data-testid={`${testIdPrefix}-input`} />
      <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testIdPrefix}-btn`}>
        {busy ? <Loader2 className="w-4 h-4 me-2 animate-spin"/> : <Paperclip className="w-4 h-4 me-2"/>}
        {busy ? "..." : "Attach"}
      </Button>
      {value?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map(f => (
            <div key={f.id} className="flex items-center gap-2 text-xs bg-muted rounded-md px-2 py-1" data-testid={`${testIdPrefix}-item-${f.id}`}>
              <FileText className="w-3 h-3 text-muted-foreground"/>
              <span className="truncate max-w-[160px]">{f.name}</span>
              <button onClick={() => remove(f.id)} className="text-muted-foreground hover:text-destructive" aria-label="remove"><X className="w-3 h-3"/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AttachmentList({ files = [], testIdPrefix = "att" }) {
  if (!files || files.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {files.map(f => {
        const id = typeof f === "string" ? f : f.id;
        const name = typeof f === "string" ? "file" : f.name;
        const url = `${api.defaults.baseURL}/files/${id}`;
        return (
          <a key={id} href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs bg-muted hover:bg-border smooth rounded-md px-2 py-1" data-testid={`${testIdPrefix}-${id}`}>
            <FileText className="w-3 h-3 text-muted-foreground"/>
            <span className="truncate max-w-[200px]">{name}</span>
          </a>
        );
      })}
    </div>
  );
}
